import React, { useState } from 'react';
import SEO from '../SEO';
import { breadcrumbSchema } from '../../utils/structuredData';

interface QuoteFormData {
  eventType: string;
  duration: string;
  attendees: string;
  location: string;
  serviceType: string;
  additionalServices: string[];
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  eventDate: string;
  specialRequirements: string;
}

const CotizadorPage: React.FC = () => {
  const [formData, setFormData] = useState<QuoteFormData>({
    eventType: '',
    duration: '',
    attendees: '',
    location: '',
    serviceType: '',
    additionalServices: [],
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    eventDate: '',
    specialRequirements: ''
  });

  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null);

  const eventTypes = [
    'Matrimonio',
    'Evento Corporativo',
    'Festival/Concierto',
    'Feria Comercial',
    'Construcción',
    'Evento Deportivo',
    'Otro'
  ];

  const durations = [
    '1 día',
    '2-3 días',
    '4-7 días',
    '1-2 semanas',
    '3-4 semanas',
    'Más de 1 mes'
  ];

  const attendeeRanges = [
    '1-50 personas',
    '51-100 personas',
    '101-200 personas',
    '201-500 personas',
    '501-1000 personas',
    'Más de 1000 personas'
  ];

  const serviceTypes = [
    'Básico (Solo baños)',
    'Estándar (Baños + operador)',
    'Premium (Baños + operador + suministros)',
    'VIP (Baños de lujo + operador + suministros premium)'
  ];

  const additionalServicesList = [
    'Operador 8 horas',
    'Operador 12 horas',
    'Suministros biodegradables',
    'Limpieza profunda',
    'Transporte especializado',
    'Mantenimiento diario'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (service: string) => {
    setFormData(prev => ({
      ...prev,
      additionalServices: prev.additionalServices.includes(service)
        ? prev.additionalServices.filter(s => s !== service)
        : [...prev.additionalServices, service]
    }));
  };

  const calculateEstimate = () => {
    let basePrice = 0;
    
    // Base price by service type
    switch (formData.serviceType) {
      case 'Básico (Solo baños)':
        basePrice = 150000;
        break;
      case 'Estándar (Baños + operador)':
        basePrice = 250000;
        break;
      case 'Premium (Baños + operador + suministros)':
        basePrice = 350000;
        break;
      case 'VIP (Baños de lujo + operador + suministros premium)':
        basePrice = 500000;
        break;
    }

    // Duration multiplier
    let durationMultiplier = 1;
    switch (formData.duration) {
      case '2-3 días':
        durationMultiplier = 2;
        break;
      case '4-7 días':
        durationMultiplier = 4;
        break;
      case '1-2 semanas':
        durationMultiplier = 8;
        break;
      case '3-4 semanas':
        durationMultiplier = 15;
        break;
      case 'Más de 1 mes':
        durationMultiplier = 25;
        break;
    }

    // Attendees multiplier
    let attendeesMultiplier = 1;
    switch (formData.attendees) {
      case '51-100 personas':
        attendeesMultiplier = 1.5;
        break;
      case '101-200 personas':
        attendeesMultiplier = 2;
        break;
      case '201-500 personas':
        attendeesMultiplier = 3;
        break;
      case '501-1000 personas':
        attendeesMultiplier = 5;
        break;
      case 'Más de 1000 personas':
        attendeesMultiplier = 8;
        break;
    }

    // Additional services
    let additionalCost = 0;
    formData.additionalServices.forEach(service => {
      switch (service) {
        case 'Operador 8 horas':
          additionalCost += 80000;
          break;
        case 'Operador 12 horas':
          additionalCost += 120000;
          break;
        case 'Suministros biodegradables':
          additionalCost += 50000;
          break;
        case 'Limpieza profunda':
          additionalCost += 40000;
          break;
        case 'Transporte especializado':
          additionalCost += 100000;
          break;
        case 'Mantenimiento diario':
          additionalCost += 60000;
          break;
      }
    });

    const totalEstimate = (basePrice * durationMultiplier * attendeesMultiplier) + additionalCost;
    setEstimatedPrice(totalEstimate);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    calculateEstimate();
    
    // Generate WhatsApp message
    const whatsappMessage = `Hola! Me gustaría solicitar una cotización:
    
Tipo de evento: ${formData.eventType}
Fecha: ${formData.eventDate}
Duración: ${formData.duration}
Asistentes: ${formData.attendees}
Ubicación: ${formData.location}
Tipo de servicio: ${formData.serviceType}
${formData.additionalServices.length > 0 ? `Servicios adicionales: ${formData.additionalServices.join(', ')}` : ''}
${formData.specialRequirements ? `Requerimientos especiales: ${formData.specialRequirements}` : ''}

Contacto: ${formData.contactName}
Teléfono: ${formData.contactPhone}
Email: ${formData.contactEmail}

Estimación generada: $${estimatedPrice?.toLocaleString()} COP`;

    const encodedMessage = encodeURIComponent(whatsappMessage);
    window.open(`https://wa.me/573507089584?text=${encodedMessage}`, '_blank');
  };

  const breadcrumbs = breadcrumbSchema([
    { name: 'Inicio', url: 'https://junisama.com.co' },
    { name: 'Cotizador', url: 'https://junisama.com.co/cotizador' }
  ]);

  return (
    <div className="cotizador-page">
      <SEO
        title="Cotizador de Baños Portátiles - Estimación Instantánea | Junisama"
        description="Calcula el costo de alquiler de baños portátiles para tu evento. Cotización instantánea y personalizada según tus necesidades. ¡Solicita tu presupuesto ahora!"
        keywords="cotizador baños portátiles, precio alquiler sanitarios, cotización eventos, presupuesto baños móviles, calculadora baños portátiles"
        url="/cotizador"
        structuredData={breadcrumbs}
      />
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Cotizador de Servicios</h1>
          <p className="page-subtitle">
            Obtén una estimación instantánea para tu evento
          </p>
        </div>
      </div>

      <div className="cotizador-container">
        <div className="container">
          <form onSubmit={handleSubmit} className="cotizador-form">
            <div className="form-section">
              <h3>Información del Evento</h3>
              <div className="form-group">
                <label htmlFor="eventType">Tipo de Evento</label>
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar tipo de evento</option>
                  {eventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="eventDate">Fecha del Evento</label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="duration">Duración</label>
                <select
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar duración</option>
                  {durations.map(duration => (
                    <option key={duration} value={duration}>{duration}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="attendees">Número de Asistentes</label>
                <select
                  id="attendees"
                  name="attendees"
                  value={formData.attendees}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar cantidad</option>
                  {attendeeRanges.map(range => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="location">Ubicación</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Ciudad, dirección o zona"
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Tipo de Servicio</h3>
              <div className="form-group">
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccionar tipo de servicio</option>
                  {serviceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Servicios Adicionales</label>
                <div className="checkbox-group">
                  {additionalServicesList.map(service => (
                    <label key={service} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.additionalServices.includes(service)}
                        onChange={() => handleCheckboxChange(service)}
                      />
                      {service}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="specialRequirements">Requerimientos Especiales</label>
                <textarea
                  id="specialRequirements"
                  name="specialRequirements"
                  value={formData.specialRequirements}
                  onChange={handleInputChange}
                  placeholder="Describe cualquier requerimiento especial para tu evento"
                  rows={3}
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Información de Contacto</h3>
              <div className="form-group">
                <label htmlFor="contactName">Nombre Completo</label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactPhone">Teléfono</label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactEmail">Email</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-industrial btn-industrial-primary">
                Generar Cotización
              </button>
            </div>

            {estimatedPrice && (
              <div className="estimate-result">
                <h3>Estimación de Precio</h3>
                <div className="estimated-price">
                  ${estimatedPrice.toLocaleString()} COP
                </div>
                <p className="estimate-note">
                  * Esta es una estimación aproximada. El precio final puede variar según ubicación y requerimientos específicos.
                </p>
                <p className="estimate-note">
                  Se ha enviado tu cotización por WhatsApp para una respuesta personalizada.
                </p>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CotizadorPage;